import React, { useEffect, useState, useRef } from 'react';

import { useFormik } from 'formik';
import { TextInput, Textarea } from "@tremor/react";
import { Typography, Button, Navbar, Switch } from '@material-tailwind/react';
import { FaSave, FaBan, FaRobot } from 'react-icons/fa';
import * as yup from 'yup';
import isEqual from 'lodash/isEqual';

import useProject from '@datahooks/admin/project/useProject.js';
import useProjectManager from '@datahooks/admin/project/useProjectManager.js';
import { useQueryClient } from 'react-query';
import useRefState from '@datahooks/useRefState.js';
import useUser from '@datahooks/useUser.js';

import AlertManager from '@components/general/AlertManager.jsx';
import ImageUploader from '@components/admin/general/ImageUploader.jsx';
import ProjectTestTypesDialog from './ProjectTestTypesDialog.jsx';
import ProjectNotificationsDialog from './ProjectNotificationsDialog.jsx';
import ProjectTagsDialog from './ProjectTagsDialog.jsx';
import LoadingIcon from '@components/navigation/LoadingIcon.jsx';
import { ErrorCard, CARD_TYPES } from '@components/cards/ErrorCard.jsx';
import { NOTIFICATIONS_ENABLED } from '@home/config';
import { IMAGE_ENTITY_TYPES } from '@datahooks/metadata/ImageValidation.js';

import PropTypes from 'prop-types';

// Define validation schema for the form
const projectSchema = yup.object().shape({
  title: yup.string().required('Please enter a project title.').max(255, 'The project title must be 255 characters or less.'),
  short_name: yup.string().required('Please enter a short name.').max(50, 'The project short name must be 50 characters or less.'),
  intro_text: yup.string().optional().max(1000, 'The intro text must be 1000 characters or less.'),
  description_image_title: yup.string().optional().max(50, 'The image title must be 50 characters or less.'),
  released_into_test: yup.date().nullable(),
  target_to_live: yup.date().nullable(),
});

const ProjectEditForm = ({ projectId, addingProject, onCancel, onUpdatedProject, organisationId }) => {
    const imageUploaderRef = useRef(null);
    const descriptionImageUploaderRef = useRef(null);

    const [imageSelected, imageSelectedRef, setImageSelected] = useRefState(false);
    const [descriptionImageSelected, descriptionImageSelectedRef, setDescriptionImageSelected] = useRefState(false);
    
    const { user: userDetails } = useUser();

    const tempImagePath = useRef(''); // Temporary storage for image path between uploads
    const queryClient = useQueryClient();

    const projectUpdated = () => {
        setSuccessMessage("Project updated successfully."); 
        
        if (addingProject) {
            formik.resetForm();
        } else {

            if (imageSelectedRef.current) {
                setImageSelected(false);
            };

            if (descriptionImageSelectedRef.current) {
                setDescriptionImageSelected(false);
            }

            tempImagePath.current = '';  // Clear the temp image path after both uploads complete
    
            refetchProject();
        }

        queryClient.invalidateQueries({ queryKey: ['projects'], exact: false });
        queryClient.invalidateQueries({ queryKey: ['admin_projects'], exact: false });

        (onUpdatedProject && typeof onUpdatedProject === 'function' && onUpdatedProject()); // Call the callback function if provided
    }

    const projectSaveFailed = (error) => {
        setErrorMessage(`Failed to save project: ${error?.errorResponse?.message || 'Unknown error'}`);
    }

    const { performAction } = useProjectManager(projectUpdated, projectSaveFailed);

    const { projectData, loadingProject, refetchProject, projectError } = useProject(projectId);
    
    const [initialValues, setInitialValues] = useState({
        title: '',
        short_name: '',
        intro_text: '',
        released_into_test: null,
        target_to_live: null,
        logo_location: null,
        description_image_title: '',
        description_image_location: null,
        ai_analysis_enabled: false,
    });

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const getDefaultInitialValues = () => ({
            title: '',
            short_name: '',
            intro_text: '',
            released_into_test: null,
            target_to_live: null,
            logo_location: null,
            description_image_title: '',
            description_image_location: null,
            ai_analysis_enabled: false,
        });

        if (addingProject) {
            const newInitialValues = getDefaultInitialValues();

            if (!isEqual(initialValues, newInitialValues)) {
                setInitialValues(newInitialValues);
            }
        } else if (projectData) {
            const newInitialValues = {
                title: projectData.title || '',
                short_name: projectData.short_name || '',
                intro_text: projectData.intro_text || '',
                released_into_test: projectData.released_into_test || null,
                target_to_live: projectData.target_to_live || null,
                logo_location: projectData.logo_location || null,
                description_image_title: projectData.description_image_title || '',
                description_image_location: projectData.description_image_location || null,
                ai_analysis_enabled: !!projectData.ai_analysis_enabled,
            };

            if (!isEqual(initialValues, newInitialValues)) {
                setInitialValues(newInitialValues);
                // Reset each image uploader independently
                if (imageUploaderRef?.current) {
                    imageUploaderRef.current.resetImage();
                }
                if (descriptionImageUploaderRef?.current) {
                    descriptionImageUploaderRef.current.resetImage();
                }
            }
        }
    }, [addingProject, projectData, initialValues]);

    const handleSubmit = async (values) => {
        try {
            await projectSchema.validate(values, { abortEarly: false });

            if (imageSelectedRef.current) {    // If logo selected, upload it first. Second upload will be triggered after first completes, if needed
                imageUploaderRef.current.uploadImage();
            } else if (descriptionImageSelectedRef.current) {
                descriptionImageUploaderRef.current.uploadImage();
            } else {
                await doSave(); // If no images to upload, just save the project directly
            }
        } catch (error) {
            const validationErrors = error.inner.reduce((acc, err) => {
                acc[err.path] = err.message;
                return acc;
            }, {});
            formik.setErrors(validationErrors);
        }
    };

    /**
     * Function to handle saving the project after image uploads
     * Either called directly from form submit if no images to upload or from image uploader after image upload completes (imagePath will be set)
     * 
     * @param {string} imagePath 
     * @param {boolean} isDescriptionImage - True if the imagePath is for the description image, false if for the logo
     */
    const doSave = async (imagePath = null, isDescriptionImage = false) => {
        const imageUploadPending = (isDescriptionImage ? imageSelectedRef.current : descriptionImageSelectedRef.current); // If we've just saved description image, check if logo is pending and vice versa

        (isDescriptionImage ? setDescriptionImageSelected(false) : setImageSelected(false)); // Clear the relevant "image selected" flag as we've just uploaded it

        if (imageUploadPending) {
            tempImagePath.current = imagePath; // Store the image path temporarily while we upload the other image

            (isDescriptionImage ? imageUploaderRef.current?.uploadImage() : descriptionImageUploaderRef.current?.uploadImage()); // Upload the other image
            return; // Exit to wait for the other image to upload
        }

        if (addingProject) {
            if (!formik.values.title || !formik.values.short_name) {
                console.error("Title and short name are required.");
                return;
            }

            await doAddProject(imagePath, isDescriptionImage);
        } else {
            await doUpdateProject(imagePath, isDescriptionImage);
        }
    };
    
    const doAddProject = async (imagePath = null, isDescriptionImage = false) => {

        const payload = {
            new_org_id: organisationId,
            new_title: formik.values.title,
            new_short_name: formik.values.short_name
        };
    
        if (formik.values.intro_text) {
            payload.new_intro_text = formik.values.intro_text;
        }
    
        if (formik.values.released_into_test) {
            payload.new_released_into_test = formik.values.released_into_test;
        }
    
        if (formik.values.target_to_live) {
            payload.new_target_to_live = formik.values.target_to_live;
        }

        payload.new_ai_analysis_enabled = formik.values.ai_analysis_enabled ? 1 : 0;

        return performAction(payload, 'PUT');
    }
    
    const doUpdateProject = async (imagePath = null, isDescriptionImage = false) => {
        const updatePayload = { project_id: projectId };
    
        const logoPath = isDescriptionImage ? tempImagePath.current : imagePath;
        const descriptionImagePath = isDescriptionImage ? imagePath : tempImagePath.current;

        // Always include non-image field changes in every save
        if (!isEqual(initialValues.title, formik.values.title)) {
            updatePayload.new_title = formik.values.title;
        }
    
        if (!isEqual(initialValues.short_name, formik.values.short_name)) {
            updatePayload.new_short_name = formik.values.short_name;
        }

        if (!isEqual(initialValues.description_image_title, formik.values.description_image_title)) {
            updatePayload.new_description_image_title = formik.values.description_image_title;
        }
    
        if (!isEqual(initialValues.intro_text, formik.values.intro_text)) {
            updatePayload.new_intro_text = formik.values.intro_text;
        }
    
        if (!isEqual(initialValues.released_into_test, formik.values.released_into_test)) {
            updatePayload.new_released_into_test = formik.values.released_into_test;
        }
    
        if (!isEqual(initialValues.target_to_live, formik.values.target_to_live)) {
            updatePayload.new_target_to_live = formik.values.target_to_live;
        }

        if (!isEqual(initialValues.ai_analysis_enabled, formik.values.ai_analysis_enabled)) {
            updatePayload.new_ai_analysis_enabled = Number(formik.values.ai_analysis_enabled);
        }

        // Add the current image path being uploaded
        if (logoPath) {
            updatePayload.new_logo_location = logoPath
        }

        if (descriptionImagePath) {
            updatePayload.new_description_image_location = descriptionImagePath;
        }
    
        // Always perform the update if we have changes or an image
        if (Object.keys(updatePayload).length > 1) {
            try {
                await performAction(updatePayload, 'POST');
            } catch (error) {
                setErrorMessage(`Failed to update project: ${error?.message || 'Unknown error'}`);
            }
        }
    }

    const handleImageSelected = (file) => {
        setImageSelected(true);
    };

    const handleDescriptionImageSelected = (file) => {
        setDescriptionImageSelected(true);
    };

    const formik = useFormik({
        initialValues,
        validationSchema: projectSchema,
        enableReinitialize: true, 
        onSubmit: handleSubmit, 
    });

    if (loadingProject) {
        return <LoadingIcon showText />;
    }

    if (projectError) {
        return <div><ErrorCard cardType={CARD_TYPES.EMPTY} /> {projectError.message}</div>; 
    }

    return (
        <>
            <Typography variant="h5" className="text-blue-500 mt-2">
                {addingProject ? 'Add New Project' : 'Edit Project'}
            </Typography>

            <form onSubmit={formik.handleSubmit} className="flex flex-col justify-center w-full max-w-4xl">
                {projectId !== -1 && !addingProject && projectData && (
                    <Typography variant="h5" className="text-blue-500 mt-4">
                        Project ID: {projectId}
                    </Typography>
                )}

                <Typography variant="h5" className="text-blue-500 mt-4">Title</Typography>
                <TextInput
                    label="Title"
                    name="title"
                    placeholder="Enter the full title of the project"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.title && formik.errors.title}
                    errorMessage={formik.errors.title}
                />

                <Typography variant="h5" className="text-blue-500 mt-4">Short name</Typography>
                <TextInput
                    label="Short Name"
                    name="short_name"
                    placeholder="Enter a short name for the project for use in the menu"
                    value={formik.values.short_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.short_name && formik.errors.short_name}
                    errorMessage={formik.errors.short_name}
                />

                {projectId !== -1 && !addingProject && projectData && (        
                    <>
                        <Typography variant="h5" className="text-blue-500 mt-4">Project Logo:</Typography>
                        <ImageUploader
                            ref={imageUploaderRef}
                            alt={formik.values.short_name + " logo"}
                            imgSrc={formik.values.logo_location || ''}
                            onSavePath={(path) => doSave(path)}
                            imageType={IMAGE_ENTITY_TYPES.project}
                            imageId={projectId}
                            onImageSelect={handleImageSelected}
                            displayType='logo'
                        />
                    </>
                )}

                <Typography variant="h5" className="text-blue-500 mt-4">Intro</Typography>
                <Textarea
                    label="Intro Text"
                    placeholder="Enter a brief introduction to the project."
                    name="intro_text"
                    autoHeight={true}
                    value={formik.values.intro_text}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.intro_text && formik.errors.intro_text}
                    errorMessage={formik.errors.intro_text}
                />

                {projectId !== -1 && !addingProject && projectData && (        
                    <div className='flex gap-4 items-center'>
                        <div className='flex-shrink-0'>
                            <Typography variant="h5" className="text-blue-500 mt-4">Description Image</Typography>
                            <ImageUploader
                                ref={descriptionImageUploaderRef}
                                alt={formik.values.short_name + " description image"}
                                imgSrc={formik.values.description_image_location || ''}
                                onSavePath={(path) => doSave(path, true)}
                                imageType={IMAGE_ENTITY_TYPES.project_description}
                                imageId={projectId}
                                onImageSelect={handleDescriptionImageSelected}
                                displayType='logo'
                            />
                        </div>
                        <div className='w-full'>
                            <Typography variant="h5" className="text-blue-500 mt-4">Description Image Title</Typography>
                            <TextInput
                                label="Description Image Title"
                                placeholder="Image title"
                                name="description_image_title"
                                type="text"
                                value={formik.values.description_image_title || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.description_image_title && formik.errors.description_image_title}
                                errorMessage={formik.errors.description_image_title}
                            />
                        </div>
                    </div>
                )}

                <Typography variant="h5" className="text-blue-500 mt-4">Released Into Test</Typography>
                <TextInput
                    label="Released Into Test"
                    placeholder="Select a date and time"
                    name="released_into_test"
                    type="date"
                    value={formik.values.released_into_test || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                <Typography variant="h5" className="text-blue-500 mt-4">Target to Live</Typography>
                <TextInput
                    label="Target to Live"
                    className='mb-4'
                    name="target_to_live"
                    type="date"
                    value={formik.values.target_to_live || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                {userDetails?.user_type === 'super_admin' && (
                    <div className="flex justify-between items-center mt-4 p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-3">
                            <div className="p-2 border border-gray-200 rounded-lg">
                                <FaRobot size={20} className="text-gray-600" />
                            </div>
                            <div>
                                <Typography variant="h6" className="text-gray-800">AI test analysis</Typography>
                                <Typography variant="small" className="text-gray-500">Enable AI analysis for failed test cases across all test types.</Typography>
                            </div>
                        </div>
                        <Switch
                            color="blue"
                            checked={formik.values.ai_analysis_enabled}
                            onChange={(e) => formik.setFieldValue('ai_analysis_enabled', e.target.checked)}
                        />
                    </div>
                )}

                <AlertManager
                    successMessage={successMessage} 
                    setSuccessMessage={setSuccessMessage} 
                    errorMessage={errorMessage} 
                    setErrorMessage={setErrorMessage} 
                />

                <Navbar className="m-0 mt-5 flex items-center justify-end w-full p-4 gap-4 bg-gray-100 border-t border-gray-300">
                    {projectId !== -1 && !addingProject && (    
                        <div className="flex justify-start gap-2">
                            <ProjectTestTypesDialog 
                                projectId={projectId}
                            />
                            {NOTIFICATIONS_ENABLED && userDetails?.user_type === 'super_admin' &&  (
                                <ProjectNotificationsDialog
                                    projectId={projectId}
                                />
                            )}
                            <ProjectTagsDialog projectId={projectId} />
                        </div>
                    )}
                    
                    <div className="flex ml-auto gap-4">
                        <Button 
                            color="blue" 
                            variant="outlined" 
                            onClick={onCancel}
                            className="flex text-blue-500 items-center gap-3"
                        >
                            <FaBan /> Cancel
                        </Button>
                        <Button 
                            color="blue" 
                            type="submit" 
                            disabled={!formik.dirty && !formik.isSubmitting && !imageSelected && !descriptionImageSelected}
                            className="flex items-center gap-3">
                            <FaSave /> Save
                        </Button>
                    </div>
                </Navbar>
            </form>

        </>
    );
};

ProjectEditForm.propTypes = {
    projectId: PropTypes.number,
    addingProject: PropTypes.bool,
    onCancel: PropTypes.func,
    onUpdatedProject: PropTypes.func,
    organisationId: PropTypes.number,
};
export default ProjectEditForm;